import React, {useEffect, useState} from 'react'
import styled from 'styled-components';
import RouteContainer from "../../../common/styles/RouteContainer";
import useAppUserProfile from "../useAppUserProfile";
import {useParams, useNavigate} from "react-router";
import {Pagination} from 'react-bootstrap';
import RowSpacer from "../../../common/styles/RowSpacer";
import BookMineCard from "../../book/mine/BookMineCard";
import NoContentForFullScreen from "../../../common/NoContentForFullScreen";
import ProfileUserCard from "./ProfileUserCard";
import useHtmlTitle from "../../../common/useHtmlTitle";
import BookMineDoneCard from "../../book/mine/BookMineDoneCard";
import Paging from "../../../common/hooks/Paging";
import useUrlQuery from "../../../common/hooks/useUrlQuery";

const ProfileRoute = () => {
    const {userId} = useParams()
    const navigate = useNavigate()
    const pageQuery = useUrlQuery('page') ?? '1'
    const orderByQuery = useUrlQuery('orderBy')

    const [orderBy, setOrderBy] = useState<'LAST_MODIFIED' | 'CREATED' | 'DONE'>('LAST_MODIFIED')

    useEffect(() => {
        if (orderByQuery && ['LAST_MODIFIED', 'CREATED', 'DONE'].includes(orderByQuery)) {
            setOrderBy(orderByQuery as 'LAST_MODIFIED' | 'CREATED' | 'DONE')
        }
    }, [orderByQuery])

    const [page, setPage] = useState<number | undefined>(undefined)
    useEffect(() => {
        const parsed = parseInt(pageQuery, 10)
        setPage(!Number.isNaN(parsed) ? parsed : 1)
    }, [pageQuery])

    const handleOrderByChange = (newOrderBy: 'LAST_MODIFIED' | 'CREATED' | 'DONE') => {
        setOrderBy(newOrderBy)
        const params = new URLSearchParams(window.location.search)
        params.set('orderBy', newOrderBy)
        params.delete('page')
        navigate(`/users/${userId}?${params.toString()}`, { replace: true })
    }

    const [appUserWithBooks, isProfileLoading, isError, paging] = useAppUserProfile(userId, page, 24, orderBy);

    useHtmlTitle(appUserWithBooks ? `${appUserWithBooks?.user.name} | 프로필` : null)

    if (isProfileLoading) {
        return <></>
    }

    if (isError || appUserWithBooks == null) {
        return <NoContentForFullScreen message={'유저가 없거나 탈퇴한 유저에요.'}/>
    }

    return (
        <RouteContainer>
            <RowSpacer/>
            <ProfileUserCard user={appUserWithBooks.user} isClickable={false}/>

            <RowSpacer size={40}/>
            <OrderByContainer>
                <OrderBySelect value={orderBy} onChange={(e) => handleOrderByChange(e.target.value as 'LAST_MODIFIED' | 'CREATED' | 'DONE')}>
                    <option value="LAST_MODIFIED">최근 수정순</option>
                    <option value="CREATED">추가한 순</option>
                    <option value="DONE">완독한 순</option>
                </OrderBySelect>
            </OrderByContainer>

            <Row>
                {
                    appUserWithBooks.books.length === 0 ?
                        <NoContentForFullScreen message={'공유 설정한 책이 없어요'}/>
                        :
                        appUserWithBooks.books.map(book =>
                            <Col key={book.id}>
                                {
                                    book.status == 'DONE' ?
                                        <BookMineDoneCard book={book}
                                                          url={book.isbn != null ? `/book/${book.isbn}` : `/users/${userId}/books/${book.id}`}/>
                                        :
                                        <BookMineCard book={book}
                                                      url={book.isbn != null ? `/book/${book.isbn}` : `/users/${userId}/books/${book.id}`}/>
                                }
                            </Col>
                        )
                }
            </Row>

            {
                appUserWithBooks.books.length !== 0 &&
                <Page paged={paging} currentPage={page ?? 1} url={`/users/${userId}`}/>
            }
            <RowSpacer size={40}/>

            <RowSpacer size={40}/>
        </RouteContainer>
    )
}

const Row = styled.div.attrs({
    className: 'row'
})``

const Col = styled.div.attrs({
    className: 'col-6 col-md-4 col-lg-3 col-xl-2'
})`
    margin-top: 30px;
    height: 300px;

    & > a {
        display: block;
        height: 100%;
    }

    .clickable {
        height: 100%;
        padding-top: 5px;
        padding-bottom: 5px;
    }
`

interface PageProps {
    paged: Paging
    currentPage: number
    url: string
}

const Page: React.FC<PageProps> = ({paged, currentPage, url}) => {

    if (paged.totalPages === 0) return <></>

    const orderByParam = new URLSearchParams(window.location.search).get('orderBy')
    const orderByQuery = orderByParam ? `&orderBy=${orderByParam}` : ''

    return (
        <div className='d-flex justify-content-center mb-5 mt-5'>
            <PaginationContainer>
                {paged.totalPages >= 6 && currentPage >= 4 && <Pagination.First href={`${url}?page=1${orderByQuery}`}/>}

                {
                    paged.totalPages <= 5 ?
                        Array.from({length: paged.totalPages}, (_, index) => index + 1).map((p) => {
                            return (
                                <Pagination.Item active={p == currentPage ?? 1} href={`${url}?page=${p}${orderByQuery}`}>
                                    {p}
                                </Pagination.Item>
                            )
                        })
                        :
                        Array.from({length: paged.totalPages}, (_, index) => index + 1).map((p) => {
                            const totalVisiblePages = 5;
                            const halfVisiblePages = Math.floor(totalVisiblePages / 2);

                            let startPage = currentPage - halfVisiblePages;
                            let endPage = currentPage + halfVisiblePages;

                            if (startPage < 1) {
                                startPage = 1;
                                endPage = totalVisiblePages;
                            }

                            if (endPage > paged.totalPages) {
                                endPage = paged.totalPages;
                                startPage = paged.totalPages - totalVisiblePages + 1;
                            }

                            return (
                                <>
                                    {
                                        startPage <= p && p <= endPage &&
                                        <Pagination.Item active={p == currentPage ?? 1} href={`${url}?page=${p}${orderByQuery}`}>
                                            {p}
                                        </Pagination.Item>
                                    }
                                </>
                            )
                        })
                }

                {paged.totalPages > 5 && currentPage <= paged.totalPages - 3 &&
                    <Pagination.Last href={`${url}?page=${paged.totalPages}${orderByQuery}`}/>}
            </PaginationContainer>
        </div>
    )
}

const PaginationContainer = styled(Pagination)`
    [data-theme='dark'] & a {
        background-color: var(--card-card-bg-color) !important;
        color: var(--text-color) !important;
        border-color: var(--card-card-bg-color) !important;
    }
`

const OrderByContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 10px;
`

const OrderBySelect = styled.select`
    padding: 14px 16px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    padding-right: 36px;
    border: none;
    border-radius: 16px;
    background-color: var(--search-field-bg-color);
    color: var(--text-color);
    font-size: 16px;
    cursor: pointer;
    outline: none;
    transition: background-color 0.2s ease;

    &:focus {
        background-color: var(--input-focus-bg-color);
    }
`

export default ProfileRoute
